import { useEffect, useState } from "react";
import { Trash2, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtNum } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { AutoRemindersPanel, RecalculatePanel, StockRepairPanel } from "@/components/StartupSafety";

const CountTable = ({ rows, testid }) => (
  <table className="w-full text-sm" data-testid={testid}>
    <tbody>
      {rows.map((r) => (
        <tr key={r.label} className="border-b border-slate-100 last:border-0">
          <td className="py-1.5 text-slate-600">{r.label}</td>
          <td className={`py-1.5 text-right font-mono ${r.count ? "text-slate-900" : "text-slate-300"}`}>{fmtNum(r.count)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function DataHousekeeping() {
  const [users, setUsers] = useState(true);
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const load = () => { setRows(null); api.get("/housekeeping/demo-preview", { params: { include_users: users } }).then((r) => setRows(r.data.rows)).catch((e) => setErr(errMsg(e))); };
  useEffect(load, [users]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = (rows || []).reduce((a, r) => a + r.count, 0);
  const run = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/housekeeping/remove-demo", { confirm, include_users: users });
      setDone(r.data.rows); setConfirm(""); toast.success("Demo and test data removed"); load();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="housekeeping-page" className="max-w-3xl">
      <PageHeader title="Data housekeeping" subtitle="Remove the demo and test records that came with the system. Only records explicitly marked as demo or test are touched; real SAP data, vendors and your own login are kept." />
      <Panel title="Remove demo data" testid="remove-demo-panel">
        <label className="mb-4 flex items-center gap-2 text-sm text-slate-700">
          <Checkbox data-testid="remove-demo-users-checkbox" checked={users} onCheckedChange={(v) => setUsers(!!v)} />
          Also remove the test logins (viewer@, plant3000@, compliance@, purchasing@, account@) and their customer assignments
        </label>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Preview: records that will be removed</p>
        {rows ? <CountTable rows={rows} testid="remove-demo-preview" /> : <div className="h-40 animate-pulse rounded-lg bg-slate-100" />}
        <div className="mt-5 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />This can't be undone. Demo/test records are exempt from the 5-year retention rule; real EUDR records are never included.</div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Input data-testid="remove-demo-confirm-input" className="w-56" placeholder="Type REMOVE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button data-testid="remove-demo-submit" variant="destructive" disabled={busy || confirm !== "REMOVE" || !rows || !total} onClick={run}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}Remove demo data ({fmtNum(total)})
          </Button>
        </div>
        {err && <p className="mt-3 text-sm text-red-600" data-testid="remove-demo-error">{err}</p>}
      </Panel>
      {done && (
        <Panel title="Removed" testid="remove-demo-result" className="mt-5">
          <CountTable rows={done.filter((r) => r.count)} testid="remove-demo-result-table" />
        </Panel>
      )}
      <AutoRemindersPanel />
      <StockRepairPanel />
      <RecalculatePanel />
    </div>
  );
}
